import { useMemo, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PaginatedTable } from '@/components/common/PaginatedTable'
import { cn } from '@/utils/cn'
import {
  isAutoCorrection,
  isMismatchError,
  type ProductListItemRead,
} from '@/api/productLists'

export type ImportRowStatus = 'update' | 'new' | 'needsFix'

interface ImportRowView {
  id: string
  row: number
  sku: string
  description: string
  status: ImportRowStatus
  notes: string
}

const STATUS: Record<
  ImportRowStatus,
  { label: string; filter: string; className: string }
> = {
  update: {
    label: 'Update',
    filter: 'Update',
    className: 'border-transparent bg-blue-50 text-blue-700',
  },
  new: {
    label: 'New item',
    filter: 'New',
    className: 'border-transparent bg-green-50 text-green-700',
  },
  needsFix: {
    label: 'Needs fixing',
    filter: 'Needs fixing',
    className: 'border-transparent bg-red-50 text-red-700',
  },
}

type Filter = 'all' | ImportRowStatus

const COLUMNS = [
  { key: 'row' as const, header: 'Row', width: '70px' },
  { key: 'sku' as const, header: 'SKU #', width: '120px' },
  { key: 'description' as const, header: 'Description' },
  {
    key: 'status' as const,
    header: 'Action',
    width: '130px',
    render: (status: ImportRowView[keyof ImportRowView]) => {
      const { label, className } = STATUS[status as ImportRowStatus]
      return (
        <Badge variant="outline" className={cn('whitespace-nowrap', className)}>
          {label}
        </Badge>
      )
    },
  },
  { key: 'notes' as const, header: 'Notes' },
]

function describeNotes(item: ProductListItemRead, toFix: number) {
  const errors = item.errors ?? []
  const corrected = errors.filter(isAutoCorrection).length
  const changed = errors.filter(
    err => !isAutoCorrection(err) && !isMismatchError(err)
  ).length
  return [
    toFix > 0 && `${toFix} to fix`,
    changed > 0 && `${changed} changed`,
    corrected > 0 && `${corrected} auto-corrected`,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** Every importable row with what the import will do to it. */
export function ImportItemsTable({
  rows,
  matchedRows,
  issuesToFixByRow,
  truncated,
}: {
  rows: ProductListItemRead[]
  matchedRows: Set<number>
  /** Number of blocking issues per file row; rows not listed are importable. */
  issuesToFixByRow: Map<number, number>
  truncated: boolean
}) {
  const [filter, setFilter] = useState<Filter>('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const views = useMemo<ImportRowView[]>(
    () =>
      rows.map(item => {
        const toFix = issuesToFixByRow.get(item.row_number) ?? 0
        const status: ImportRowStatus =
          toFix > 0
            ? 'needsFix'
            : matchedRows.has(item.row_number)
              ? 'update'
              : 'new'
        return {
          id: item.id,
          row: item.row_number,
          sku: item.parsed_data?.plan_sku_num ?? '—',
          description: item.parsed_data?.description ?? '—',
          status,
          notes: describeNotes(item, toFix),
        }
      }),
    [rows, matchedRows, issuesToFixByRow]
  )

  const counts = useMemo(() => {
    const result: Record<Filter, number> = {
      all: views.length,
      update: 0,
      new: 0,
      needsFix: 0,
    }
    for (const view of views) result[view.status] += 1
    return result
  }, [views])

  const filtered =
    filter === 'all' ? views : views.filter(view => view.status === filter)
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize)
  const filters: Filter[] = ['all', 'update', 'new', 'needsFix']

  return (
    <div className="space-y-3">
      <div
        role="group"
        aria-label="Filter rows"
        className="flex flex-wrap gap-1.5"
      >
        {filters
          .filter(key => key === 'all' || counts[key] > 0)
          .map(key => (
            <Button
              key={key}
              variant={filter === key ? 'secondary' : 'outline'}
              size="sm"
              aria-pressed={filter === key}
              className="h-7 rounded-full px-3"
              onClick={() => {
                setFilter(key)
                setPage(1)
              }}
            >
              {key === 'all' ? 'All' : STATUS[key].filter} · {counts[key]}
            </Button>
          ))}
      </div>
      <div className="overflow-hidden rounded-lg border [&_td]:py-3">
        <PaginatedTable<ImportRowView>
          columns={COLUMNS}
          data={pageItems}
          page={page}
          pageSize={pageSize}
          totalCount={filtered.length}
          totalPages={Math.max(1, Math.ceil(filtered.length / pageSize))}
          pageSizeOptions={[10, 25, 50]}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          getRowKey={view => view.id}
          itemLabel="rows"
          emptyMessage="No rows in the uploaded file."
        />
      </div>
      {truncated && (
        <p className="text-xs text-[#556374]">
          Showing the first 5,000 rows of the file.
        </p>
      )}
    </div>
  )
}
